import { useCallback, useEffect, useState } from "react";
import { api, galleryHeaders } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

export function useFavorites() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [favorites, setFavorites] = useState([]);

  const load = useCallback(async () => {
    if (!user) return setFavorites([]);
    try {
      const { data } = await api.get("/favorites");
      setFavorites(data.media_ids || []);
    } catch {
      setFavorites([]);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = useCallback(
    async (media) => {
      if (!user) {
        toast.error(t("gallery.loginToFavorite"));
        return;
      }
      const has = favorites.includes(media.id);
      setFavorites((prev) => (has ? prev.filter((id) => id !== media.id) : [...prev, media.id]));
      try {
        if (has) await api.delete(`/favorites/${media.id}`);
        else await api.post(`/favorites/${media.id}`);
      } catch {
        load();
      }
    },
    [favorites, user, load, t]
  );

  const addMany = useCallback(
    async (ids) => {
      if (!user) {
        toast.error(t("gallery.loginToFavorite"));
        return;
      }
      await Promise.all(ids.map((id) => api.post(`/favorites/${id}`).catch(() => null)));
      load();
    },
    [user, load, t]
  );

  return { favorites, toggleFavorite: toggle, addManyFavorites: addMany, reloadFavorites: load };
}

export async function downloadZip(payload, slug) {
  const res = await api.post("/download/zip", payload, {
    responseType: "blob",
    headers: galleryHeaders(slug),
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = "hr-creations.zip";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
